// @ts-nocheck
/**
 * ダンジョン生成メインモジュール
 * 2枚のカードからダンジョンを生成
 * @module twinKey/core/dungeon/dungeonGenerator
 */

import { selectContinent, calculateTotalScore, calculateAttributeSynergy } from './continentSelector';
import { calculateDepth, calculateEnemyLevel } from './depthCalculator';
import { generateVariation, seededRandom } from './variationGenerator';
import { generateFloorChoices } from './floorGenerator';
import { getContinent, getDepthLevel } from './data/continents';
import { BOSS_FLOOR_INTERVAL } from '../../data/constants';

// ============================================================
// メイン関数
// ============================================================

/**
 * ダンジョン生成メイン関数
 * @param {Object} key1 - 鍵カード1
 * @param {Object} key2 - 鍵カード2
 * @returns {Object} Dungeon
 */
export function generateDungeon(key1, key2) {
  // シード値生成（再現性確保）
  const seed = hashCode(key1.cardId + '_' + key2.cardId + '_' + key1.rarity + '_' + key2.rarity);

  // 大陸決定
  const continentId = selectContinent(key1.attribute, key2.attribute);
  const continent = getContinent(continentId);

  // 深さ・階層数決定
  const depthResult = calculateDepth(key1.rarity, key2.rarity);
  const depthInfo = getDepthLevel(depthResult.depth);

  // バリエーション決定
  const variation = generateVariation(seed, continent);

  // ボス階層計算（10Fごと）
  const bossFloors = [];
  for (let f = BOSS_FLOOR_INTERVAL; f <= depthResult.floors; f += BOSS_FLOOR_INTERVAL) {
    bossFloors.push(f);
  }

  // 敵レベル計算
  const [minLevel, maxLevel] = depthResult.enemyLevelRange;
  const baseEnemyLevel = minLevel;
  const levelScaling = (maxLevel - minLevel) / depthResult.floors;

  // 属性シナジー
  const synergy = calculateAttributeSynergy(key1.attribute, key2.attribute);
  const totalScore = calculateTotalScore(key1.attribute, key2.attribute);

  return {
    // 識別子
    id: `dungeon_${key1.cardId}_${key2.cardId}`,
    seed,

    // 鍵情報
    keys: {
      key1: {
        cardId: key1.cardId,
        name: key1.name,
        attribute: key1.attribute,
        rarity: key1.rarity,
      },
      key2: {
        cardId: key2.cardId,
        name: key2.name,
        attribute: key2.attribute,
        rarity: key2.rarity,
      },
      synergy,
      totalScore,
    },

    // 基本情報
    name: `${continent.name}・${depthInfo.name}・${variation.background}`,
    continent: continentId,
    continentName: continent.name,
    depth: depthResult.depth,
    depthName: depthInfo.name,
    background: variation.background,
    themeColor: continent.themeColor,

    // 構造
    maxFloor: depthResult.floors,
    bossFloors,

    // 難易度
    difficulty: depthResult.difficulty,
    baseEnemyLevel,
    levelScaling,
    rarityTotal: depthResult.rarityTotal,

    // 特性
    enemyBias: variation.enemyBias,
    gimmick: variation.gimmick,
    rewardBonus: variation.rewardBonus,
    timeOfDay: variation.timeOfDay,

    // フロア情報（動的生成）
    floors: [],

    // 進行状態
    currentFloor: 0,
    isCompleted: false,
    isFailed: false,

    // 統計（クリア後に更新）
    stats: {
      battlesWon: 0,
      battlesLost: 0,
      totalDamageDealt: 0,
      totalDamageTaken: 0,
      itemsFound: 0,
      goldEarned: 0,
      expEarned: 0,
    },
  };
}

/**
 * ダンジョンプレビューを生成（探索前の情報表示用）
 * @param {Object} key1 - 鍵カード1
 * @param {Object} key2 - 鍵カード2
 * @returns {Object} DungeonPreview
 */
export function generateDungeonPreview(key1, key2) {
  const seed = hashCode(key1.cardId + '_' + key2.cardId + '_' + key1.rarity + '_' + key2.rarity);

  const continentId = selectContinent(key1.attribute, key2.attribute);
  const continent = getContinent(continentId);

  const depthResult = calculateDepth(key1.rarity, key2.rarity);
  const depthInfo = getDepthLevel(depthResult.depth);

  const variation = generateVariation(seed, continent);
  const synergy = calculateAttributeSynergy(key1.attribute, key2.attribute);

  return {
    name: `${continent.name}・${depthInfo.name}・${variation.background}`,
    continent: continentId,
    continentName: continent.name,
    depth: depthResult.depth,
    depthName: depthInfo.name,
    floors: depthResult.floors,
    difficulty: depthResult.difficulty,
    enemyBias: variation.enemyBias,
    gimmick: variation.gimmick,
    rewardBonus: variation.rewardBonus,
    timeOfDay: variation.timeOfDay,
    themeColor: continent.themeColor,
    synergy,
  };
}

/**
 * 次のフロアの選択肢を取得
 * @param {Object} dungeon - ダンジョン
 * @returns {Array} フロア選択肢
 */
export function getNextFloorChoices(dungeon) {
  const nextFloor = dungeon.currentFloor + 1;
  if (nextFloor > dungeon.maxFloor) {
    return null; // ダンジョンクリア
  }
  return generateFloorChoices(dungeon, nextFloor);
}

/**
 * フロアを進行
 * @param {Object} dungeon - ダンジョン
 * @param {Object} selectedChoice - 選択したフロア
 * @returns {Object} 更新されたダンジョン
 */
export function advanceFloor(dungeon, selectedChoice) {
  const newFloor = dungeon.currentFloor + 1;

  return {
    ...dungeon,
    currentFloor: newFloor,
    floors: [
      ...dungeon.floors,
      {
        number: newFloor,
        choice: selectedChoice,
        completed: false,
      },
    ],
  };
}

/**
 * フロア完了を記録
 * @param {Object} dungeon - ダンジョン
 * @param {Object} result - フロア結果
 * @returns {Object} 更新されたダンジョン
 */
export function completeFloor(dungeon, result) {
  const currentFloorData = dungeon.floors[dungeon.floors.length - 1];

  const updatedFloors = [...dungeon.floors];
  updatedFloors[updatedFloors.length - 1] = {
    ...currentFloorData,
    completed: true,
    result,
  };

  // 統計更新
  const updatedStats = { ...dungeon.stats };
  if (result.type === 'battle_won') {
    updatedStats.battlesWon += 1;
    updatedStats.goldEarned += result.gold || 0;
    updatedStats.expEarned += result.exp || 0;
    updatedStats.totalDamageDealt += result.damageDealt || 0;
    updatedStats.totalDamageTaken += result.damageTaken || 0;
  } else if (result.type === 'battle_lost') {
    updatedStats.battlesLost += 1;
  } else if (result.type === 'treasure') {
    updatedStats.itemsFound += result.itemCount || 1;
    updatedStats.goldEarned += result.gold || 0;
  }

  // ダンジョンクリア判定
  const isCompleted = dungeon.currentFloor >= dungeon.maxFloor && result.type !== 'battle_lost';
  const isFailed = result.type === 'battle_lost' || result.type === 'party_wiped';

  return {
    ...dungeon,
    floors: updatedFloors,
    stats: updatedStats,
    isCompleted,
    isFailed,
  };
}

/**
 * ダンジョン結果サマリーを生成
 * @param {Object} dungeon - 完了したダンジョン
 * @returns {Object} サマリー
 */
export function generateDungeonSummary(dungeon) {
  const { stats, maxFloor, currentFloor, isCompleted, difficulty } = dungeon;

  // 評価計算（0-3星）
  let rating = 1; // ベース1星
  if (isCompleted) {
    rating += 1; // クリアで2星
    if (stats.battlesLost === 0) {
      rating += 1; // ノーコンティニューで3星
    }
  }

  // 進行度
  const progress = Math.floor((currentFloor / maxFloor) * 100);

  return {
    // ResultScreenが期待するフィールド
    dungeonName: dungeon.name,
    floorReached: currentFloor,
    totalFloors: maxFloor,
    difficulty,
    // 進行度情報
    progress: `${progress}%（${currentFloor}/${maxFloor}階）`,
    rating,
    // 統計情報
    stats: {
      ...stats,
      battlesTotal: (stats.battlesWon || 0) + (stats.battlesLost || 0),
      averageDamageDealt: stats.battlesWon > 0
        ? Math.floor(stats.totalDamageDealt / stats.battlesWon)
        : 0,
    },
    // 報酬情報
    rewards: {
      gold: stats.goldEarned,
      exp: stats.expEarned,
      items: stats.itemsFound || [],
      bonusMultiplier: rating === 3 ? 2.0 : (rating === 2 ? 1.5 : 1.0),
    },
    // パーティ状態（呼び出し側で追加する必要がある）
    partyState: [],
  };
}

// ============================================================
// ユーティリティ
// ============================================================

/**
 * シンプルなハッシュ関数
 * @param {string} str - 入力文字列
 * @returns {number} ハッシュ値
 */
function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * ダンジョンの有効性をチェック
 * @param {Object} dungeon - ダンジョン
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateDungeon(dungeon) {
  if (!dungeon) {
    return { valid: false, reason: 'ダンジョンが未生成です' };
  }

  if (dungeon.isCompleted) {
    return { valid: false, reason: 'このダンジョンは既にクリア済みです' };
  }

  if (dungeon.isFailed) {
    return { valid: false, reason: 'このダンジョンは失敗しています' };
  }

  return { valid: true };
}

/**
 * ダンジョン状態をリセット
 * @param {Object} dungeon - ダンジョン
 * @returns {Object} リセットされたダンジョン
 */
export function resetDungeon(dungeon) {
  return {
    ...dungeon,
    currentFloor: 0,
    floors: [],
    isCompleted: false,
    isFailed: false,
    stats: {
      battlesWon: 0,
      battlesLost: 0,
      totalDamageDealt: 0,
      totalDamageTaken: 0,
      itemsFound: 0,
      goldEarned: 0,
      expEarned: 0,
    },
  };
}
